import type {
  CreateObjectionInput,
  FeedbackJudgment,
  FeedbackObjection,
  JudgeEntryInput,
  ProjectFeedbackDetail,
  ProjectFeedbackRevision,
  ProjectFeedbackSummary,
} from '../../types/project-feedback.js';
import { request } from './_request.js';

/**
 * FB の ID をパスに入れる形にする（ID はメールを含むので必ずエンコードする）。
 * @graph-connects none
 */
function feedbackPath(feedbackId: string, suffix = ''): string {
  return `/project-feedbacks/${encodeURIComponent(feedbackId)}${suffix}`;
}

/**
 * 案件 FB API クライアント。閲覧範囲・異議・判断の可否は API が決め、ここは口を叩くだけ。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering, HR
 * @graph-business API client for project feedback: own list, scoped list, detail, revisions, owner objections and evaluator judgments
 * @graph-connects /project-feedbacks [calls, via:GET] 見える範囲の FB 一覧
 * @graph-connects /project-feedbacks/mine [calls, via:GET] 自分の FB 一覧
 * @graph-connects /project-feedbacks/:feedbackId [calls, via:GET] FB の詳細
 * @graph-connects /project-feedbacks/:feedbackId/revisions [calls, via:GET] FB の過去の版
 * @graph-connects /project-feedbacks/:feedbackId/objections [calls, via:POST] 異議の作成
 * @graph-connects /project-feedbacks/:feedbackId/objections/:objectionId/withdraw [calls, via:POST] 異議の取り下げ
 * @graph-connects /project-feedbacks/:feedbackId/judgments [calls, via:PUT] 判断の作成・更新
 * @graph-connects feedbackPath [calls] FB の ID をパスへエンコード
 */
export const projectFeedbackApi = {
  /** 見える範囲の FB 一覧（本人の FB を含む）。 */
  list: async (): Promise<{ feedbacks: ProjectFeedbackSummary[] }> =>
    request<{ feedbacks: ProjectFeedbackSummary[] }>('/project-feedbacks'),
  /** 自分の FB 一覧。 */
  listMine: async (): Promise<{ feedbacks: ProjectFeedbackSummary[] }> =>
    request<{ feedbacks: ProjectFeedbackSummary[] }>('/project-feedbacks/mine'),
  /** 詳細。見えない FB は 404。 */
  getDetail: async (feedbackId: string): Promise<ProjectFeedbackDetail> =>
    request<ProjectFeedbackDetail>(feedbackPath(feedbackId)),
  /** 全版（新しい順）。 */
  listRevisions: async (feedbackId: string): Promise<{ revisions: ProjectFeedbackRevision[] }> =>
    request<{ revisions: ProjectFeedbackRevision[] }>(feedbackPath(feedbackId, '/revisions')),
  /** 本人が項目に異議を出す。 */
  createObjection: async (
    feedbackId: string,
    input: CreateObjectionInput,
  ): Promise<{ objection: FeedbackObjection }> =>
    request<{ objection: FeedbackObjection }>(feedbackPath(feedbackId, '/objections'), {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  /** 本人が異議を取り下げる。 */
  withdrawObjection: async (
    feedbackId: string,
    objectionId: string,
  ): Promise<{ objection: FeedbackObjection }> =>
    request<{ objection: FeedbackObjection }>(
      feedbackPath(feedbackId, `/objections/${encodeURIComponent(objectionId)}/withdraw`),
      { method: 'POST' },
    ),
  /** 評価者が項目に判断を付ける・付け直す。 */
  judge: async (
    feedbackId: string,
    input: JudgeEntryInput,
  ): Promise<{ judgment: FeedbackJudgment }> =>
    request<{ judgment: FeedbackJudgment }>(feedbackPath(feedbackId, '/judgments'), {
      method: 'PUT',
      body: JSON.stringify(input),
    }),
};
